import { TableComplete } from '@e3dc-react/shell/Elements/Table'
import { Hidden, List, ListItem, ListItemText, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Header } from '@e3dc-react/shell/Elements/Table/Table'
import { CheckboxField } from '@e3dc-react/shell/Elements/InputFields'
import { BatteryTables, tableColumnsOptions, BatteryExportModule } from './BatteryExportUtils'
import { makeStyles } from 'tss-react/mui'
import { MultiSelect } from '@e3dc-react/shell/Elements/Select'
import { LabeledOption } from 'src/Types/globalTypes'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ParseDetailsReturnType } from '@e3dc-react/shell/Hooks/useValidation/useValidation'

const useStyles = makeStyles()(theme => ({
  listView: {
    display: 'flex',
    flexDirection: 'row',
    paddingRight: 0,
    paddingLeft: 0,
  },
  multiSelect: {
    color: 'green',
  },
  tableContainer: {
    display: 'flex',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      width: '40%',
      alignItems: 'center',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      width: '50%',
      alignItems: 'start',
    },
  },
  columnContainer: {
    justifyContent: 'end',
    width: '60%',
  },
}))

interface BatteryDataSelectionProps {
  batteryExport: BatteryExportModule
  setBatteryExport: (batteryExport: BatteryExportModule) => void
  validation: ParseDetailsReturnType
}

/**
 * Component allows user to specefiy tables and columns to export battery data from
 * @param batteryExport stores relevant battery export data to send to backend
 */
const BatteryDataSelection: React.FC<BatteryDataSelectionProps> = ({ batteryExport, setBatteryExport, validation }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const [tableData, setTableData] = useState<BatteryTables>({
    batterydata: {
      columns: ['all'],
      isSelected: true,
    },
    batterydataExLG: {
      columns: ['all'],
      isSelected: true,
    },
    dcbdata: {
      columns: ['all'],
      isSelected: true,
    },
    dcbconfiguration: {
      columns: ['all'],
      isSelected: true,
    },
    batteryconfiguration: {
      columns: ['all'],
      isSelected: true,
    },
  })

  useEffect(() => {
    setBatteryExport({ ...batteryExport, tables: tableData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData])

  const onChangeColumns = (value: string[], tableKey: string, index: number): void => {
    const selectedColumns = !value ? [] : value.includes('all') ? value.filter(column => column === 'all') : value
    setTableData({ ...tableData, [tableKey]: { columns: selectedColumns ?? [], isSelected: Object.values(tableData)[index].isSelected } })
  }

  const tableColumns: Header<BatteryTables>[] = [
    {
      value: 'Aktiv',
      label: t('batteryDataSelection.active'),
      valueRender: (value, index, data) => {
        const tableKey = Object.keys(tableData)[index]
        return (
          <CheckboxField
            value={Object.values(tableData)[index].isSelected}
            onChange={value => setTableData({ ...tableData, [tableKey]: { columns: Object.values(tableData)[index].columns, isSelected: value } })}
          />
        )
      },
    },
    {
      value: 'Tabelle',
      label: t('batteryDataSelection.table'),
      valueRender: (value, index, data) => {
        const tableKey = Object.keys(tableData)[index]
        return <Typography>{tableKey}</Typography>
      },
    },
    {
      value: 'Spalten',
      label: t('batteryDataSelection.columns'),
      valueRender: (value, index, data) => {
        const tableKey = Object.keys(tableData)[index]
        const tableColumnOptions: LabeledOption<string>[] = tableColumnsOptions[index]
        return (
          <MultiSelect
            value={Object.values(tableData)[index].columns}
            className={classes.multiSelect}
            options={tableColumnOptions}
            onChange={value => onChangeColumns(value, tableKey, index)}
            helperText={validation[`tables.${tableKey}.columns`]?.message}
          />
        )
      },
    },
  ]

  const ListView: React.FC = () => {
    return (
      <List>
        {Object.values(tableData).map((table, index) => {
          const tableKey = Object.keys(tableData)[index]
          const tableColumnOptions: LabeledOption<string>[] = tableColumnsOptions[index]

          return (
            <ListItem className={classes.listView} key={`tableData-listItem-${index}`}>
              <div className={classes.tableContainer}>
                <CheckboxField
                  value={Object.values(tableData)[index].isSelected}
                  onChange={value =>
                    setTableData({ ...tableData, [tableKey]: { columns: Object.values(tableData)[index].columns, isSelected: value } })
                  }
                />
                <ListItemText primary={tableKey} />
              </div>

              <div className={classes.columnContainer}>
                <MultiSelect
                  value={Object.values(tableData)[index].columns}
                  className={classes.multiSelect}
                  options={tableColumnOptions}
                  onChange={value => onChangeColumns(value, tableKey, index)}
                />
              </div>
            </ListItem>
          )
        })}
      </List>
    )
  }

  return (
    <div>
      <Hidden mdDown>
        <TableComplete header={tableColumns} data={Object.values(tableData)} disablePagination headerElevation={0} bodyElevation={0} />
      </Hidden>
      <Hidden mdUp>
        <ListView />
      </Hidden>
    </div>
  )
}

export default BatteryDataSelection
